import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, ChevronDown, Quote, FileText, BookOpen, HelpCircle } from 'lucide-react';

interface ContentItem {
  id: string;
  title?: string;
  content: string;
  author?: string;
  date?: string;
  image?: string;
  subtitle?: string;
  expanded?: boolean;
}

interface PremiumContentProps {
  variant: 'magazine' | 'blog' | 'feature' | 'faq' | 'quote';
  title?: string;
  subtitle?: string;
  items: ContentItem[];
  columns?: number;
  backgroundColor?: string;
  textColor?: string;
  accentColor?: string;
  titleSize?: string;
  titleAlign?: string;
  contentSize?: string;
  contentAlign?: string;
  lineHeight?: string;
  contentWeight?: string;
  showDivider?: boolean;
  imagePosition?: 'left' | 'right' | 'top';
}

const PremiumContent: React.FC<PremiumContentProps> = ({
  variant = 'magazine',
  title,
  subtitle,
  items = [],
  columns = 1,
  backgroundColor = 'transparent',
  textColor = '#1f2937',
  accentColor = '#3b82f6',
  titleSize = 'text-3xl',
  titleAlign = 'left',
  contentSize = 'text-base',
  contentAlign = 'left',
  lineHeight = 'normal',
  contentWeight = 'normal',
  showDivider = true,
  imagePosition = 'left'
}) => {
  const [expandedItems, setExpandedItems] = React.useState<{[key: string]: boolean}>({});

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  // Helper functions for dynamic classes
  const getTitleSizeClass = (size?: string): string => {
    switch(size) {
      case 'text-xl': return 'text-xl';
      case 'text-2xl': return 'text-2xl';
      case 'text-3xl': return 'text-3xl';
      case 'text-4xl': return 'text-4xl';
      case 'text-5xl': return 'text-5xl';
      case 'text-6xl': return 'text-6xl';
      default: return 'text-3xl';
    }
  };

  const getTextAlignClass = (align?: string): string => {
    switch(align) {
      case 'left': return 'text-left';
      case 'center': return 'text-center';
      case 'right': return 'text-right';
      case 'justify': return 'text-justify';
      default: return 'text-left';
    }
  };

  const getContentSizeClass = (size?: string): string => {
    switch(size) {
      case 'text-sm': return 'text-sm';
      case 'text-base': return 'text-base';
      case 'text-lg': return 'text-lg';
      case 'text-xl': return 'text-xl';
      default: return 'text-base';
    }
  };

  const getLineHeightClass = (height?: string): string => {
    switch(height) {
      case 'tight': return 'leading-tight';
      case 'snug': return 'leading-snug';
      case 'normal': return 'leading-normal';
      case 'relaxed': return 'leading-relaxed';
      case 'loose': return 'leading-loose';
      default: return 'leading-normal';
    }
  };

  const getFontWeightClass = (weight?: string): string => {
    switch(weight) {
      case 'light': return 'font-light';
      case 'normal': return 'font-normal';
      case 'medium': return 'font-medium';
      case 'semibold': return 'font-semibold';
      case 'bold': return 'font-bold';
      default: return 'font-normal';
    }
  };

  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  // Magazine Style - Multi-column article layout with drop caps
  const renderMagazine = () => (
    <div className={`grid grid-cols-1 md:grid-cols-${columns} gap-6`}>
      {items.map((item, index) => (
        <motion.article
          key={item.id}
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 prose prose-lg max-w-none"
          variants={itemAnimation}
        >
          {item.title && (
            <h3 className={`${getTitleSizeClass(titleSize)} font-bold mb-4`} style={{ color: textColor }}>
              {item.title}
            </h3>
          )}
          {item.subtitle && (
            <p className="text-gray-600 italic mb-4">{item.subtitle}</p>
          )}
          <div
            className={`${getContentSizeClass(contentSize)} ${getLineHeightClass(lineHeight)} ${getFontWeightClass(contentWeight)} ${getTextAlignClass(contentAlign)}`}
            style={{ color: textColor }}
            dangerouslySetInnerHTML={{ __html: item.content }}
          />
          {item.author && (
            <p className="mt-4 text-sm text-gray-500">— {item.author}</p>
          )}
        </motion.article>
      ))}
    </div>
  );

  // Blog Style - Clean article layout
  const renderBlog = () => (
    <div className="max-w-4xl mx-auto">
      {items.map((item, index) => (
        <motion.article
          key={item.id}
          className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-8"
          variants={itemAnimation}
        >
          {item.image && (
            <div className="w-full">
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-64 object-cover"
              />
            </div>
          )}
          <div className="p-6">
            {item.date && (
              <p className="text-sm text-gray-500 mb-2">{item.date}</p>
            )}
            {item.title && (
              <h2 className={`${getTitleSizeClass(titleSize)} font-bold mb-4`} style={{ color: textColor }}>
                {item.title}
              </h2>
            )}
            <div
              className={`${getContentSizeClass(contentSize)} ${getLineHeightClass(lineHeight)} ${getFontWeightClass(contentWeight)} ${getTextAlignClass(contentAlign)} prose prose-lg max-w-none`}
              style={{ color: textColor }}
              dangerouslySetInnerHTML={{ __html: item.content }}
            />
            {item.author && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600">Written by {item.author}</p>
              </div>
            )}
          </div>
        </motion.article>
      ))}
    </div>
  );

  // Feature Story - Image + Text side by side
  const renderFeature = () => (
    <div className="space-y-8">
      {items.map((item, index) => (
        <motion.div
          key={item.id}
          className={`bg-white rounded-xl shadow-lg border border-gray-100 p-8 flex flex-col md:flex-row gap-8 items-center ${
            imagePosition === 'right' && index % 2 === 0 ? 'md:flex-row-reverse' : ''
          }`}
          variants={itemAnimation}
        >
          {item.image && (
            <div className="md:w-1/2">
              <img
                src={item.image}
                alt={item.title}
                className="rounded-xl w-full"
              />
            </div>
          )}
          <div className="md:w-1/2">
            {item.title && (
              <h2 className={`${getTitleSizeClass(titleSize)} font-bold mb-4`} style={{ color: textColor }}>
                {item.title}
              </h2>
            )}
            {item.subtitle && (
              <p className="text-xl text-gray-600 mb-4">{item.subtitle}</p>
            )}
            <div
              className={`${getContentSizeClass(contentSize)} ${getLineHeightClass(lineHeight)} ${getFontWeightClass(contentWeight)} ${getTextAlignClass(contentAlign)} prose max-w-none`}
              style={{ color: textColor }}
              dangerouslySetInnerHTML={{ __html: item.content }}
            />
          </div>
        </motion.div>
      ))}
    </div>
  );

  // FAQ Style - Collapsible Q&A
  const renderFAQ = () => (
    <div className="max-w-3xl mx-auto space-y-3">
      {items.map((item, index) => (
        <motion.div
          key={item.id}
          className="bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden"
          variants={itemAnimation}
        >
          <button
            onClick={() => toggleExpanded(item.id)}
            className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-4">
              <HelpCircle className="w-6 h-6 flex-shrink-0" style={{ color: accentColor }} />
              <h3 className="text-lg font-semibold text-left" style={{ color: textColor }}>
                {item.title}
              </h3>
            </div>
            {expandedItems[item.id] ? (
              <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
            )}
          </button>
          {expandedItems[item.id] && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="px-6 pb-6 border-t border-gray-100"
            >
              <div
                className={`${getContentSizeClass(contentSize)} ${getLineHeightClass(lineHeight)} ${getFontWeightClass(contentWeight)} ${getTextAlignClass(contentAlign)} pt-4 prose max-w-none`}
                style={{ color: textColor }}
                dangerouslySetInnerHTML={{ __html: item.content }}
              />
            </motion.div>
          )}
        </motion.div>
      ))}
    </div>
  );

  // Quote Style - Testimonial/Quote blocks
  const renderQuote = () => (
    <div className={`grid grid-cols-1 md:grid-cols-${columns} gap-8`}>
      {items.map((item) => (
        <motion.blockquote
          key={item.id}
          className="relative p-8 bg-white rounded-xl shadow-lg"
          variants={itemAnimation}
        >
          <Quote
            className="absolute top-4 left-4 w-8 h-8 opacity-20"
            style={{ color: accentColor }}
          />
          <div
            className={`${getContentSizeClass(contentSize)} ${getLineHeightClass(lineHeight)} ${getFontWeightClass(contentWeight)} ${getTextAlignClass(contentAlign)} italic mb-4 relative z-10`}
            style={{ color: textColor }}
            dangerouslySetInnerHTML={{ __html: item.content }}
          />
          {(item.author || item.title) && (
            <footer className="mt-4">
              {item.author && (
                <cite className="block font-semibold not-italic" style={{ color: textColor }}>
                  {item.author}
                </cite>
              )}
              {item.title && (
                <span className="text-sm text-gray-600">{item.title}</span>
              )}
            </footer>
          )}
        </motion.blockquote>
      ))}
    </div>
  );

  const renderContent = () => {
    switch(variant) {
      case 'magazine':
        return renderMagazine();
      case 'blog':
        return renderBlog();
      case 'feature':
        return renderFeature();
      case 'faq':
        return renderFAQ();
      case 'quote':
        return renderQuote();
      default:
        return renderMagazine();
    }
  };

  return (
    <motion.section
      className="py-12 px-4"
      style={{ backgroundColor }}
      initial="hidden"
      animate="visible"
      variants={containerAnimation}
    >
      <div className="container mx-auto">
        {title && (
          <motion.div className={`mb-8 ${getTextAlignClass(titleAlign)}`} variants={itemAnimation}>
            <h2 className={`${getTitleSizeClass(titleSize)} font-bold`} style={{ color: textColor }}>
              {title}
            </h2>
            {subtitle && (
              <p className="text-xl mt-2 text-gray-600">{subtitle}</p>
            )}
          </motion.div>
        )}
        {renderContent()}
      </div>
    </motion.section>
  );
};

export default PremiumContent;